import React, { useState, useEffect } from 'react';
import { ComponentBatch } from '../../../types/componentBatch';
import { Recipe } from '../../../types/recipe';
import { ComponentIngredientsTable } from './ComponentIngredientsTable';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import {
  fetchComponentBatchIngredientsByComponentBatchId,
  createComponentBatchIngredient,
  updateComponentBatchIngredient,
  deleteComponentBatchIngredient
} from '../../../store/slices/componentBatchIngredientSlice';
import { ComponentIngredient } from '../../../types/componentRecord';
import { CreateComponentBatchIngredientRequest, UpdateComponentBatchIngredientRequest } from '../../../types/componentBatchIngredient';
import { componentApiService } from '../../../services/componentApiService';

interface ComponentRecordCardProps {
  record: ComponentBatch;
  components: Recipe[];
  isEditMode: boolean;
  onUpdate: (record: ComponentBatch) => void;
  onSaveNew?: (record: ComponentBatch, ingredients: ComponentIngredient[]) => void;
  onDelete: () => void;
  onEditToggle: () => void;
  onTimesProducedChange?: (id: string, timesProduced: number) => void;
}

export const ComponentRecordCard: React.FC<ComponentRecordCardProps> = ({
  record,
  components,
  isEditMode,
  onUpdate,
  onSaveNew,
  onDelete,
  onEditToggle,
  onTimesProducedChange
}) => {
  const dispatch = useAppDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isIngredientsExpanded, setIsIngredientsExpanded] = useState(false);
  const [editedRecord, setEditedRecord] = useState<ComponentBatch>(record);

  // Local state for ingredients during creation/editing (for temp IDs)
  const [localIngredients, setLocalIngredients] = useState<ComponentIngredient[]>([]);

  // Redux state for ingredients (for persisted records)
  const reduxIngredients = useAppSelector(state =>
    state.componentBatchIngredients.ingredients.filter(
      ing => ing.componentBatchId === record.id
    )
  );

  // Get all raw ingredients for looking up rawIngredientId
  const allRawIngredients = useAppSelector(state => state.rawIngredients.ingredients);

  // Determine if this is a new/temporary record
  const isTempRecord = record.id.startsWith('temp-');

  // Get current timesProduced with fallback to 1
  const currentTimesProduced = record.timesProduced ?? 1;

  const handleTimesProducedChange = (newValue: number) => {
    // Ensure minimum value of 1
    const value = Math.max(1, newValue);
    if (onTimesProducedChange && !isTempRecord) {
      onTimesProducedChange(record.id, value);
    }
  };

  // Fetch ingredients when card is for a persisted record (non-temp ID)
  useEffect(() => {
    if (!isTempRecord && record.id) {
      dispatch(fetchComponentBatchIngredientsByComponentBatchId(record.id));
    }
  }, [dispatch, record.id, isTempRecord]);

  // Convert Redux ingredients to local format for display
  const displayIngredients: ComponentIngredient[] = isTempRecord
    ? localIngredients
    : reduxIngredients.map(ing => ({
        id: ing.id,
        ingredientName: ing.rawIngredientName || '',
        lotNumbers: ing.lotNumbers,
        quantity: ing.quantity ?? undefined,
        unit: ing.unit ?? undefined
      }));

  const generateBatchLotNumber = (productCode: string, date: string): string => {
    if (!productCode || !date) return '';

    // Convert date to YYYYMMDD format
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;

    return `${productCode}-${dateStr}-C`;
  };

  const handleFieldChange = (field: keyof ComponentBatch, value: string) => {
    if (field === 'productId') {
      // When product changes, auto-populate productName and regenerate batch/lot number
      const selectedComponent = components.find(c => c.id === value);
      const productCode = selectedComponent?.productCode || '';
      const currentDate = editedRecord.dateOfProduction || new Date().toISOString().split('T')[0];

      setEditedRecord({
        ...editedRecord,
        productId: value,
        productName: selectedComponent?.name || '',
        batchLotNumber: generateBatchLotNumber(productCode, currentDate)
      });
    } else {
      setEditedRecord({
        ...editedRecord,
        [field]: value
      });
    }
  };

  const handleProductNameChange = async (value: string) => {
    // Find the component by name
    const selectedComponent = components.find(c => c.name === value);

    if (selectedComponent) {
      const productCode = selectedComponent.productCode || '';
      const currentDate = editedRecord.dateOfProduction || new Date().toISOString().split('T')[0];

      setEditedRecord({
        ...editedRecord,
        productId: selectedComponent.id,
        productName: selectedComponent.name,
        batchLotNumber: generateBatchLotNumber(productCode, currentDate)
      });

      // Auto-populate ingredients from recipe (only for new/temp records)
      if (isTempRecord) {
        try {
          const recipe = await componentApiService.fetchComponentRecipe(selectedComponent.id);
          const prePopulatedIngredients: ComponentIngredient[] = recipe.ingredients.map((ing, index) => ({
            id: `temp-ing-${Date.now()}-${index}`,
            ingredientName: ing.ingredientName,
            quantity: ing.quantity,
            unit: ing.unit,
            lotNumbers: []
          }));
          setLocalIngredients(prePopulatedIngredients);
        } catch (error) {
          console.error('Error fetching component recipe:', error);
        }
      }
    } else {
      // If typing but not selected from list, just update the productName
      setEditedRecord({
        ...editedRecord,
        productName: value
      });
    }
  };

  const handleSave = () => {
    // For new records, use onSaveNew to pass ingredients
    if (isTempRecord && onSaveNew) {
      onSaveNew(editedRecord, localIngredients);
    } else {
      onUpdate(editedRecord);
    }
    onEditToggle();
  };

  const handleCancel = () => {
    setEditedRecord(record);
    onEditToggle();
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box'
  };

  return (
    <div
      style={{
        border: '2px solid #ddd',
        borderRadius: '8px',
        marginBottom: '16px',
        backgroundColor: 'white',
        overflow: 'hidden'
      }}
    >
      {/* Card Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderBottom: isExpanded ? '1px solid #ddd' : 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#eeeeee')}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
      >
        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>{record.productName || 'New Component Record'}</h4>
        <span
          style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#666',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block'
          }}
        >
          ▼
        </span>
      </div>

      {/* Card Content */}
      <div style={{ padding: '20px' }}>
        {/* Collapsed View - Table of Fields */}
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '16px' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}
          >
          <thead>
            <tr>
              <th
                style={{
                  padding: '12px 8px',
                  backgroundColor: '#f8f9fa',
                  fontWeight: '600',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  textTransform: 'uppercase',
                  fontSize: '12px',
                  letterSpacing: '0.5px',
                  color: '#555'
                }}
              >
                Product Name
              </th>
              <th
                style={{
                  padding: '12px 8px',
                  backgroundColor: '#f8f9fa',
                  fontWeight: '600',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  textTransform: 'uppercase',
                  fontSize: '12px',
                  letterSpacing: '0.5px',
                  color: '#555'
                }}
              >
                Date of Production
              </th>
              <th
                style={{
                  padding: '12px 8px',
                  backgroundColor: '#f8f9fa',
                  fontWeight: '600',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  textTransform: 'uppercase',
                  fontSize: '12px',
                  letterSpacing: '0.5px',
                  color: '#555'
                }}
              >
                Batch/Lot #
              </th>
              <th
                style={{
                  padding: '12px 8px',
                  backgroundColor: '#f8f9fa',
                  fontWeight: '600',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  textTransform: 'uppercase',
                  fontSize: '12px',
                  letterSpacing: '0.5px',
                  color: '#555'
                }}
              >
                Prepared By
              </th>
              <th
                style={{
                  padding: '12px 8px',
                  backgroundColor: '#f8f9fa',
                  fontWeight: '600',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  textTransform: 'uppercase',
                  fontSize: '12px',
                  letterSpacing: '0.5px',
                  color: '#555'
                }}
              >
                Verified By
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                {isEditMode ? (
                  <>
                    <input
                      type="text"
                      list="component-list"
                      value={editedRecord.productName || ''}
                      onChange={e => handleProductNameChange(e.target.value)}
                      style={inputStyle}
                      placeholder="Search for a component..."
                    />
                    <datalist id="component-list">
                      {components.map(component => (
                        <option key={component.id} value={component.name} />
                      ))}
                    </datalist>
                  </>
                ) : (
                  record.productName || '-'
                )}
              </td>
              <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                {isEditMode ? (
                  <input
                    type="date"
                    value={editedRecord.dateOfProduction}
                    readOnly
                    style={{
                      ...inputStyle,
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed'
                    }}
                  />
                ) : (
                  record.dateOfProduction ? record.dateOfProduction.split('T')[0] : '-'
                )}
              </td>
              <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                {isEditMode ? (
                  <input
                    type="text"
                    value={editedRecord.batchLotNumber}
                    readOnly
                    style={{
                      ...inputStyle,
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed'
                    }}
                    placeholder="Select product to generate"
                  />
                ) : (
                  record.batchLotNumber || '-'
                )}
              </td>
              <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                {isEditMode ? (
                  <input
                    type="text"
                    value={editedRecord.preparedBy}
                    onChange={e => handleFieldChange('preparedBy', e.target.value)}
                    style={inputStyle}
                    placeholder="Prepared by"
                  />
                ) : (
                  record.preparedBy || '-'
                )}
              </td>
              <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                {isEditMode ? (
                  <input
                    type="text"
                    value={editedRecord.verifiedBy}
                    onChange={e => handleFieldChange('verifiedBy', e.target.value)}
                    style={inputStyle}
                    placeholder="Verified by"
                  />
                ) : (
                  record.verifiedBy || '-'
                )}
              </td>
            </tr>
          </tbody>
        </table>
        </div>

        {/* Expanded View - Ingredients and Preparation Section */}
        {isExpanded && (
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                marginBottom: '16px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                overflow: 'hidden'
              }}
            >
              <div
                onClick={() => setIsIngredientsExpanded(!isIngredientsExpanded)}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: isIngredientsExpanded ? '1px solid #e0e0e0' : 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#555',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#eeeeee')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
              >
                Ingredients and Preparation
                <span
                  style={{
                    fontSize: '1rem',
                    color: '#666',
                    transition: 'transform 0.2s ease-in-out',
                    transform: isIngredientsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block'
                  }}
                >
                  ▶
                </span>
              </div>
              {isIngredientsExpanded && (
                <div
                  style={{
                    padding: '20px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <ComponentIngredientsTable
                    ingredients={displayIngredients}
                    isEditing={isEditMode}
                    onChange={(newIngredients) => {
                      // Only update local state for temp records
                      if (isTempRecord) {
                        setLocalIngredients(newIngredients);
                        return;
                      }

                      // For persisted records, detect changes and dispatch Redux actions
                      const oldIngredients = displayIngredients;

                      // Detect deletions: old ingredients that don't exist in new list
                      oldIngredients.forEach(oldIng => {
                        const stillExists = newIngredients.some(newIng => newIng.id === oldIng.id);

                        if (!stillExists) {
                          // Only delete if it's a persisted ingredient (has UUID, not temp ID)
                          const reduxIng = reduxIngredients.find(ri => ri.id === oldIng.id);
                          if (reduxIng) {
                            dispatch(deleteComponentBatchIngredient(oldIng.id));
                          }
                        }
                      });

                      // Detect additions and updates
                      newIngredients.forEach(newIng => {
                        // Find the rawIngredientId
                        const rawIngredient = allRawIngredients.find(ri => ri.name === newIng.ingredientName);

                        if (!rawIngredient) {
                          console.error(`Raw ingredient not found for name: ${newIng.ingredientName}`);
                          return;
                        }

                        // Check if this is a new ingredient (temp ID or not in Redux)
                        const reduxIng = reduxIngredients.find(ri => ri.id === newIng.id);

                        if (!reduxIng) {
                          // This is a new ingredient - create it
                          const createRequest: CreateComponentBatchIngredientRequest = {
                            componentBatchId: record.id,
                            rawIngredientId: rawIngredient.id,
                            lotNumbers: Array.isArray(newIng.lotNumbers) ? newIng.lotNumbers : [newIng.lotNumbers],
                            quantity: newIng.quantity ?? null,
                            unit: newIng.unit ?? null
                          };
                          dispatch(createComponentBatchIngredient(createRequest));
                        } else {
                          // Check if it was updated
                          const lotNumbersChanged = JSON.stringify(reduxIng.lotNumbers) !== JSON.stringify(newIng.lotNumbers);
                          const quantityChanged = reduxIng.quantity !== newIng.quantity;
                          const unitChanged = reduxIng.unit !== newIng.unit;
                          const ingredientChanged = reduxIng.rawIngredientId !== rawIngredient.id;

                          if (lotNumbersChanged || quantityChanged || unitChanged || ingredientChanged) {
                            const updateRequest: UpdateComponentBatchIngredientRequest = {
                              rawIngredientId: rawIngredient.id,
                              lotNumbers: Array.isArray(newIng.lotNumbers) ? newIng.lotNumbers : [newIng.lotNumbers],
                              quantity: newIng.quantity ?? null,
                              unit: newIng.unit ?? null
                            };
                            dispatch(updateComponentBatchIngredient({ id: newIng.id, data: updateRequest }));
                          }
                        }
                      });
                    }}
                    componentId={editedRecord.productId}
                    timesProduced={currentTimesProduced}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons and Times Produced Control */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isEditMode ? (
              <>
                <button
                  onClick={handleSave}
                  style={{
                    ...buttonStyle,
                    backgroundColor: '#4CAF50',
                    color: 'white'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  style={{
                    ...buttonStyle,
                    backgroundColor: '#757575',
                    color: 'white'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onEditToggle}
                  style={{
                    ...buttonStyle,
                    backgroundColor: '#2196F3',
                    color: 'white'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1976D2')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#2196F3')}
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Delete this component record? This cannot be undone.')) {
                      onDelete();
                    }
                  }}
                  style={{
                    ...buttonStyle,
                    backgroundColor: '#f44336',
                    color: 'white'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f44336')}
                >
                  Delete
                </button>
              </>
            )}
          </div>

          {/* Times Produced Control - always visible, not dependent on edit mode */}
          {!isTempRecord && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#555', whiteSpace: 'nowrap' }}>
                # of times produced:
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTimesProducedChange(currentTimesProduced - 1);
                  }}
                  disabled={currentTimesProduced <= 1}
                  style={{
                    ...buttonStyle,
                    padding: '4px 10px',
                    backgroundColor: currentTimesProduced <= 1 ? '#cccccc' : '#757575',
                    color: 'white',
                    cursor: currentTimesProduced <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                  onMouseEnter={e => {
                    if (currentTimesProduced > 1) {
                      e.currentTarget.style.backgroundColor = '#616161';
                    }
                  }}
                  onMouseLeave={e => {
                    if (currentTimesProduced > 1) {
                      e.currentTarget.style.backgroundColor = '#757575';
                    }
                  }}
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  value={currentTimesProduced}
                  onClick={(e) => e.stopPropagation()}
                  onChange={e => {
                    e.stopPropagation();
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value)) {
                      handleTimesProducedChange(value);
                    }
                  }}
                  style={{
                    width: '50px',
                    padding: '4px 8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTimesProducedChange(currentTimesProduced + 1);
                  }}
                  style={{
                    ...buttonStyle,
                    padding: '4px 10px',
                    backgroundColor: '#757575',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
                >
                  +
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
